import React, { useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, ShieldCheckIcon, LockIcon, TagIcon, FileTextIcon, AlertTriangleIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { useData } from '../../contexts/DataContext';
import { Role } from '../../types';
export function Roles() {
  const {
    roles,
    addRole,
    updateRole,
    deleteRole
  } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState('');
  const filteredRoles = (roles || []).filter(role => role.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) || role.name?.toLowerCase().includes(searchTerm.toLowerCase()) || role.description?.toLowerCase().includes(searchTerm.toLowerCase()));
  const openNewForm = () => {
    setSelectedRole(null);
    setFormData({
      name: '',
      displayName: '',
      description: ''
    });
    setFormErrors({});
    setSuccessMessage('');
    setIsFormOpen(true);
  };
  const openEditForm = (role: Role) => {
    if (role.isSystemRole) return;
    setSelectedRole(role);
    setFormData({
      name: role.name,
      displayName: role.displayName,
      description: role.description || ''
    });
    setFormErrors({});
    setSuccessMessage('');
    setIsFormOpen(true);
  };
  const openDeleteConfirm = (role: Role) => {
    if (role.isSystemRole) return;
    setSelectedRole(role);
    setIsDeleteConfirmOpen(true);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors({});
    setSuccessMessage('');
    // Validation
    const errors: Record<string, string> = {};
    if (!formData.displayName.trim()) {
      errors.displayName = 'El nombre visible es requerido';
    }
    if (!selectedRole && !formData.name.trim()) {
      errors.name = 'El nombre interno es requerido';
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setIsSubmitting(true);
    try {
      if (selectedRole) {
        await updateRole(selectedRole.id, {
          displayName: formData.displayName,
          description: formData.description
        });
        setSuccessMessage('Rol actualizado correctamente');
      } else {
        await addRole({
          name: formData.name.toLowerCase().replace(/\s+/g, '_'),
          displayName: formData.displayName,
          description: formData.description,
          isSystemRole: false
        });
        setSuccessMessage('Rol creado correctamente');
      }
      // Close modal after short delay to show success message
      setTimeout(() => {
        setIsFormOpen(false);
        setSuccessMessage('');
      }, 1000);
    } catch (error) {
      console.error('Error saving role:', error);
      setFormErrors({
        submit: 'Error al guardar el rol. Por favor intenta de nuevo.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleDelete = async () => {
    if (!selectedRole) return;
    setIsSubmitting(true);
    try {
      await deleteRole(selectedRole.id);
      setIsDeleteConfirmOpen(false);
    } catch (error) {
      console.error('Error deleting role:', error);
      alert('Error al eliminar el rol');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="space-y-6" data-id="element-1819">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-1820">
        <div data-id="element-1821">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-1822">
            <ShieldCheckIcon className="w-6 h-6 text-sky-600 dark:text-sky-400" data-id="element-1823" />
            Roles
          </h2>
          <p className="text-gray-500 dark:text-gray-400" data-id="element-1824">
            Gestión de roles del sistema
          </p>
        </div>
        <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1826" />} onClick={openNewForm} data-id="element-1825">
          Nuevo Rol
        </Button>
      </div>

      {/* Search */}
      <Card data-id="element-1827">
        <div className="flex items-center gap-4" data-id="element-1828">
          <div className="flex-1 relative" data-id="element-1829">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1830" />
            <Input placeholder="Buscar roles..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-id="element-1831" />
          </div>
        </div>
      </Card>

      {/* Roles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1832">
        {filteredRoles.length === 0 ? <Card className="col-span-full" data-id="element-1833">
            <div className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-1834">
              {roles.length === 0 ? 'No hay roles creados. Haz clic en "Nuevo Rol" para crear uno.' : 'No se encontraron roles con ese criterio de búsqueda'}
            </div>
          </Card> : filteredRoles.map(role => <Card key={role.id} className="hover:shadow-lg transition-shadow" data-id="element-1835">
              <div className="p-6" data-id="element-1836">
                <div className="flex items-start justify-between mb-4" data-id="element-1837">
                  <div className="flex items-center gap-3" data-id="element-1838">
                    <div className="w-12 h-12 rounded-lg bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center" data-id="element-1839">
                      <ShieldCheckIcon className="w-6 h-6 text-sky-600 dark:text-sky-400" data-id="element-1840" />
                    </div>
                    <div data-id="element-1841">
                      <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-1842">
                        {role.displayName}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1843">
                        {role.name}
                      </p>
                    </div>
                  </div>
                  {role.isSystemRole && <Badge variant="info" data-id="element-1844">
                      <LockIcon className="w-3 h-3 mr-1" data-id="element-1845" />
                      Sistema
                    </Badge>}
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 min-h-[40px]" data-id="element-1846">
                  {role.description || 'Sin descripción'}
                </p>

                <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-1847">
                  {role.isSystemRole ? <div className="flex-1 text-center text-sm text-gray-400 dark:text-gray-500 py-2" data-id="element-1848">
                      Rol del sistema (no editable)
                    </div> : <>
                      <Button variant="outline" size="sm" leftIcon={<EditIcon className="w-4 h-4" data-id="element-1850" />} onClick={() => openEditForm(role)} className="flex-1" data-id="element-1849">
                        Editar
                      </Button>
                      <Button variant="danger" size="sm" leftIcon={<TrashIcon className="w-4 h-4" data-id="element-1852" />} onClick={() => openDeleteConfirm(role)} className="flex-1" data-id="element-1851">
                        Eliminar
                      </Button>
                    </>}
                </div>
              </div>
            </Card>)}
      </div>

      {/* Role Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => !isSubmitting && setIsFormOpen(false)} title={selectedRole ? 'Editar Rol' : 'Nuevo Rol'} size="md" icon={<ShieldCheckIcon className="w-5 h-5" data-id="element-1854" />} accentColor="purple" data-id="element-1853">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1855">
          {successMessage && <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-600 dark:text-green-400 text-sm flex items-center gap-2" data-id="element-1856">
              <ShieldCheckIcon className="w-4 h-4" data-id="element-1857" />
              {successMessage}
            </div>}

          {/* Identity Section */}
          <div data-id="element-1858">
            <div className="flex items-center gap-2 mb-4" data-id="element-1859">
              <span className="w-1.5 h-5 rounded-full bg-purple-500" data-id="element-1860"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-1861">
                Identificación del Rol
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-1862">
              {!selectedRole && <Input label="Nombre Interno (único)" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} error={formErrors.name} placeholder="ej: gerente_ventas" helperText="Solo letras minúsculas, números y guiones bajos" disabled={isSubmitting} data-id="element-1863" />}
              <Input label="Nombre Visible" value={formData.displayName} onChange={e => setFormData({
              ...formData,
              displayName: e.target.value
            })} error={formErrors.displayName} placeholder="ej: Gerente de Ventas" disabled={isSubmitting} data-id="element-1864" />
            </div>
          </div>

          {/* Description Section */}
          <div data-id="element-1865">
            <div className="flex items-center gap-2 mb-4" data-id="element-1866">
              <span className="w-1.5 h-5 rounded-full bg-indigo-500" data-id="element-1867"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-1868">
                Descripción
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-100 dark:border-gray-700/50" data-id="element-1869">
              <Textarea label="Descripción del Rol" value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} rows={3} placeholder="Describe las responsabilidades de este rol..." disabled={isSubmitting} data-id="element-1870" />
            </div>
          </div>

          {formErrors.submit && <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm flex items-center gap-2" data-id="element-1871">
              <AlertTriangleIcon className="w-4 h-4" data-id="element-1872" />
              {formErrors.submit}
            </div>}

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-1873">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} disabled={isSubmitting} data-id="element-1874">
              Cancelar
            </Button>
            <Button type="submit" isLoading={isSubmitting} data-id="element-1875">
              {selectedRole ? 'Guardar Cambios' : 'Crear Rol'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={() => !isSubmitting && setIsDeleteConfirmOpen(false)} title="Eliminar Rol" size="sm" icon={<AlertTriangleIcon className="w-5 h-5" data-id="element-1877" />} accentColor="rose" data-id="element-1876">
        <div className="space-y-4" data-id="element-1878">
          <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg" data-id="element-1879">
            <AlertTriangleIcon className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" data-id="element-1880" />
            <div data-id="element-1881">
              <p className="text-sm font-medium text-red-800 dark:text-red-200" data-id="element-1882">
                ¿Estás seguro de que deseas eliminar este rol?
              </p>
              <p className="text-sm text-red-600 dark:text-red-400 mt-1" data-id="element-1883">
                Esta acción no se puede deshacer. Los usuarios con este rol
                perderán sus permisos.
              </p>
            </div>
          </div>

          {selectedRole && <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg" data-id="element-1884">
              <div className="flex items-center gap-3" data-id="element-1885">
                <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center" data-id="element-1886">
                  <ShieldCheckIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" data-id="element-1887" />
                </div>
                <div data-id="element-1888">
                  <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-1889">
                    {selectedRole.displayName}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1890">
                    {selectedRole.description || 'Sin descripción'}
                  </p>
                </div>
              </div>
            </div>}

          <div className="flex justify-end gap-3 pt-2" data-id="element-1891">
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)} disabled={isSubmitting} data-id="element-1892">
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} isLoading={isSubmitting} data-id="element-1893">
              Eliminar Rol
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}